import { useCallback } from "react";
import { toast } from "sonner";

import { fetchFastApi } from "../api/fastapi";
import { useHookCache } from "../lib/hookCache";
import { trailCacheKeys } from "./trailCacheKeys";
import { Citation } from "../models/audit/citation";

interface UseTrailImportCitationProps {
  auditSessionId: string;
  attributeTestResultId?: string;
  sourceRowId?: string;
}

/**
 * Import an agent (reperformance) citation into the auditor trail. Copies the
 * source citation — and its screenshot sibling sharing the encoded string — onto
 * the chosen L2 `auditor_step` via the import endpoint, then invalidates the trail
 * caches so the Workpaper Review panel surfaces it. Re-importing the same evidence
 * is allowed and always creates a fresh copy. Returns the freshly created trail
 * citations so the caller can jump to and select them.
 */
export function useTrailImportCitation({
  auditSessionId,
  attributeTestResultId,
  sourceRowId,
}: UseTrailImportCitationProps) {
  const { invalidate } = useHookCache();

  return useCallback(
    async (
      sourceCitationId: string,
      auditorStepId: string,
    ): Promise<Citation[]> => {
      if (!attributeTestResultId) return [];
      try {
        const result = await fetchFastApi({
          method: "POST",
          path: `/matching/attribute-test-results/${attributeTestResultId}/auditor-steps/${auditorStepId}/import-citation`,
          body: { source_citation_id: sourceCitationId },
        });
        trailCacheKeys(
          auditSessionId,
          attributeTestResultId,
          sourceRowId,
        ).forEach((key) => void invalidate(key));

        const raw = Array.isArray(result?.created) ? result.created : [];
        const created = raw.flatMap((c: unknown) => {
          const parsed = Citation.safeParse(c);
          return parsed.success ? [parsed.data] : [];
        });
        toast.success("Citation added to Workpaper Review");
        return created;
      } catch {
        toast.error("Failed to add citation to Workpaper Review");
        return [];
      }
    },
    [auditSessionId, attributeTestResultId, sourceRowId, invalidate],
  );
}
